import { z } from 'zod';
import { IssueTimelineEventTypeEnum } from '@issuemanager/constants';

export const rawIssueTimelineSchema = z.object({
  id: z.string(),
  issue_id: z.string(),
  event_type: z.nativeEnum(IssueTimelineEventTypeEnum),
  actor_user_id: z.string(),
  event_at: z.union([z.number(), z.string()]).transform((n) => new Date(n)),
  body_data: z.object({
    v: z.string(),
    parameters: z.object({
      timestamp: z.object({
        value: z.string(),
        type: z.enum(['timestamp']),
      }),
      actorName: z.object({
        value: z.string(),
        type: z.enum(['string']),
      }),
      actorInitials: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      issueCode: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      issueOwnerId: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      issueOwnerName: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      issueDescription: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      issueStatus: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      actionTitle: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      actionId: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      issueComment: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      discardMessage: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      reason: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      editedField: z
        .object({
          value: z.string(),
          type: z.enum(['string']),
        })
        .optional(),
      editedFrom: z
        .object({
          value: z.string().nullable(),
          type: z.enum(['string', 'null']),
        })
        .optional(),
      editedTo: z
        .object({
          value: z.string().nullable(),
          type: z.enum(['string', 'null']),
        })
        .optional(),
      attachments: z
        .object({
          value: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
              hbDriveId: z.string(),
              size: z.number(),
              createdAt: z.string(),
              addedBy: z.string(),
            }),
          ),
          type: z.enum(['array']),
        })
        .optional(),
    }),
  }),
});
export type RawIssueTimelineSchema = z.infer<typeof rawIssueTimelineSchema>;

export const issueTimelineEventDatabaseSchema = z
  .object({
    id: z.string(),
    issue_id: z.string(),
    event_type: z.nativeEnum(IssueTimelineEventTypeEnum),
    actor_user_id: z.string(),
    event_at: z.union([z.number(), z.string()]).transform((n) => new Date(n)),
    body_data: z.object({
      v: z.string(),
      parameters: z.object({
        timestamp: z.object({
          value: z.string(),
          type: z.enum(['timestamp']),
        }),
        actorName: z.object({
          value: z.string(),
          type: z.enum(['string']),
        }),
        actorInitials: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        issueCode: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        issueOwnerId: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        issueOwnerName: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        issueDescription: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        issueStatus: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        actionTitle: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        actionId: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        issueComment: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        discardMessage: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        reason: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        editedField: z
          .object({
            value: z.string(),
            type: z.enum(['string']),
          })
          .optional(),
        editedFrom: z
          .object({
            value: z.string().nullable(),
            type: z.enum(['string', 'null']),
          })
          .optional(),
        editedTo: z
          .object({
            value: z.string().nullable(),
            type: z.enum(['string', 'null']),
          })
          .optional(),
        attachments: z
          .object({
            value: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
                hbDriveId: z.string(),
                size: z.number(),
                createdAt: z.string(),
                addedBy: z.string(),
              }),
            ),
            type: z.enum(['array']),
          })
          .optional(),
      }),
    }),
  })
  .transform((data) => ({
    id: data.id,
    eventType: data.event_type,
    actorId: data.actor_user_id,
    eventAt: data.event_at,
    bodyData: data.body_data,
  }));

export type IssueTimelineEventEntity = z.infer<
  typeof issueTimelineEventDatabaseSchema
>;

export const minimalIssueTimelineSchema = rawIssueTimelineSchema
  .pick({
    id: true,
    event_type: true,
    actor_user_id: true,
    event_at: true,
    body_data: true,
  })
  .transform((data) => ({
    id: data.id,
    eventType: data.event_type,
    actorId: data.actor_user_id,
    eventAt: data.event_at,
    bodyData: data.body_data,
  }));

export type MinimalIssueTimelineEntity = z.infer<
  typeof minimalIssueTimelineSchema
>;
